'use client';

import Link from 'next/link';

export default function RefundPolicyPage() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .policy-root {
          font-family: 'DM Sans', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1a2e;
        }
        .policy-nav {
          background: #0d1b2a;
          padding: 0 32px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .policy-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .policy-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #00b4d8, #0077b6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .policy-logo-text { font-size: 20px; font-weight: 700; color: #ffffff; }
        .policy-logo-text span { color: #00b4d8; }
        .policy-back {
          color: #00b4d8;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: opacity 0.2s;
        }
        .policy-back:hover { opacity: 0.8; }
        .policy-hero {
          background: linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 100%);
          padding: 60px 32px;
          text-align: center;
        }
        .policy-hero h1 { font-size: 40px; font-weight: 700; color: #fff; margin: 0 0 12px; }
        .policy-hero p { color: #8b9ab0; font-size: 15px; margin: 0; }
        .policy-content { max-width: 860px; margin: 0 auto; padding: 60px 32px; }
        .policy-card {
          background: #fff;
          border-radius: 16px;
          padding: 48px;
          border: 1px solid #e8ecf0;
          box-shadow: 0 4px 24px rgba(0,0,0,0.04);
        }
        .refund-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin: 24px 0;
        }
        .refund-step {
          background: #f8f9fb;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          border: 1px solid #e8ecf0;
        }
        .refund-step-num {
          width: 40px;
          height: 40px;
          background: #0d1b2a;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          margin: 0 auto 12px;
        }
        .refund-step h3 { font-size: 15px; font-weight: 700; color: #0d1b2a; margin: 0 0 6px; }
        .refund-step p { font-size: 13px; color: #6b7280; margin: 0; line-height: 1.5; }
        .policy-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 32px 0 12px;
          padding-top: 32px;
          border-top: 1px solid #f0f4f8;
        }
        .policy-card h2:first-of-type { margin-top: 0; padding-top: 0; border-top: none; }
        .policy-card p, .policy-card li { font-size: 15px; line-height: 1.8; color: #4b5563; margin: 0 0 12px; }
        .policy-card ul { padding-left: 20px; margin: 0 0 16px; }
        .policy-card li { margin-bottom: 8px; }
        .policy-updated {
          display: inline-block;
          background: rgba(0,180,216,0.1);
          border: 1px solid rgba(0,180,216,0.3);
          color: #00b4d8;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 99px;
          margin-bottom: 32px;
        }
        .policy-footer {
          background: #0d1b2a;
          padding: 24px 32px;
          text-align: center;
          color: #8b9ab0;
          font-size: 13px;
        }
        .policy-footer a { color: #00b4d8; text-decoration: none; }
        .policy-footer a:hover { text-decoration: underline; }
      `}</style>

      <div className="policy-root">
        <nav className="policy-nav">
          <Link href="/" className="policy-logo">
            <div className="policy-logo-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <span className="policy-logo-text">Cam<span>Smart</span></span>
          </Link>
          <Link href="/" className="policy-back">← Back to Store</Link>
        </nav>

        <div className="policy-hero">
          <h1>Refund Policy</h1>
          <p>30-day hassle-free returns on all products</p>
        </div>

        <div className="policy-content">
          <div className="policy-card">
            <span className="policy-updated">Last Updated: May 9, 2026</span>

            <h2>Our Return Process</h2>
            <div className="refund-steps">
              <div className="refund-step">
                <div className="refund-step-num">1</div>
                <h3>Contact Us</h3>
                <p>Email us within 30 days of delivery with your order number</p>
              </div>
              <div className="refund-step">
                <div className="refund-step-num">2</div>
                <h3>Ship It Back</h3>
                <p>Pack the item securely and ship to our returns center</p>
              </div>
              <div className="refund-step">
                <div className="refund-step-num">3</div>
                <h3>Get Refunded</h3>
                <p>Receive your refund within 5-10 business days</p>
              </div>
            </div>

            <h2>Return Eligibility</h2>
            <p>To be eligible for a return, your item must meet the following conditions:</p>
            <ul>
              <li>Item must be returned within 30 days of the delivery date</li>
              <li>Item must be in its original, unused condition</li>
              <li>Item must be in the original packaging with all accessories included</li>
              <li>Proof of purchase (order confirmation email) is required</li>
              <li>Item must not show signs of physical damage caused by misuse</li>
            </ul>

            <h2>Non-Returnable Items</h2>
            <p>The following items cannot be returned:</p>
            <ul>
              <li>Items returned more than 30 days after delivery</li>
              <li>Items that have been installed and show signs of use</li>
              <li>Items with missing original packaging or accessories</li>
              <li>Downloadable software or digital products</li>
              <li>Items damaged due to misuse, accidents, or unauthorized modifications</li>
              <li>Clearance or final sale items (marked as such at time of purchase)</li>
            </ul>

            <h2>Defective or Damaged Items</h2>
            <p>If you receive a defective or damaged item, please contact us within 7 days of delivery. We will arrange for a free return shipping label and send you a replacement at no additional cost, or issue a full refund including original shipping charges.</p>

            <h2>Refund Processing</h2>
            <p>Once we receive and inspect your return, we will notify you of the approval or rejection of your refund. If approved:</p>
            <ul>
              <li>Refunds are processed within 5-10 business days</li>
              <li>Refunds are issued to the original payment method</li>
              <li>Credit card refunds may take an additional 3-5 business days to appear on your statement</li>
              <li>Original shipping charges are non-refundable unless the return is due to our error</li>
            </ul>

            <h2>Return Shipping</h2>
            <p>Customers are responsible for return shipping costs unless the return is due to a defective product or our error. We recommend using a trackable shipping service and purchasing shipping insurance for items over $50. CamSmart is not responsible for items lost or damaged during return shipping.</p>

            <h2>Exchanges</h2>
            <p>We only replace items if they are defective or damaged. If you need to exchange a product for a different model, please return the original item for a refund and place a new order.</p>

            <h2>Contact Us</h2>
            <p>To initiate a return or for questions about our refund policy, contact us at:<br /><strong>CamSmart Returns</strong><br />Email: returns@camsmart.com<br />Phone: 1-800-CAM-SMART<br />Hours: Monday–Friday, 9am–6pm EST</p>
          </div>
        </div>

        <footer className="policy-footer">
          <p>© 2026 CamSmart. All rights reserved. &nbsp;|&nbsp; <Link href="/privacy-policy">Privacy Policy</Link> &nbsp;|&nbsp; <Link href="/terms-of-service">Terms of Service</Link> &nbsp;|&nbsp; <Link href="/disclaimer">Disclaimer</Link></p>
        </footer>
      </div>
    </>
  );
}
